import React, { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Message } from '../models/message';
import { UserDetailsCard } from '../Components';
import { useChatHistoryQuery, chatKeys } from './useChatQuery';
import { ChatHistory } from '../mappers/mappers';
import { reconcileMessages } from '../services/reconcile';
import { useQueryParam } from '../../../Hooks/useQueryParams';
import { useJwt } from '../../../Hooks/useJwt';
import { useChatToken } from '../../../Hooks/useChatToken';
import { showErrorBanner } from '../../../Components/ErrorBanner';
import { ChatService } from '../services/chatService';
import { isTokenExpired, onSessionExpired } from '../../../ApiClientArchitecture/session';
import { clearChatToken, getChatToken, setChatToken } from '../../../ApiClientArchitecture/tokenStore';
import { ChatStrings, SignInNoticeKey } from '../i18n';

/** Tenant ids compare case-insensitively: a link may carry an upper-case GUID, the token a lower-case one. */
const isSameTenant = (a?: string | null, b?: string | null): boolean => !!a && !!b && a.toLowerCase() === b.toLowerCase();

interface UseChatInitializationProps {
   setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
   /** The current interface strings (read at the moment a notice is raised, so it follows the language). */
   getStrings: () => ChatStrings;
}

export const useChatInitialization = ({ setMessages, getStrings }: UseChatInitializationProps) => {
   const { hotel } = useQueryParam('hotel');
   const token = useChatToken();
   const { decodeJwt } = useJwt();
   const hasInitiated = useRef(false);
   const queryClient = useQueryClient();
   const getStringsRef = useRef(getStrings);
   getStringsRef.current = getStrings;

   // A session only counts for the hotel in the link, and only while valid (an expired token would only earn a 401)
   const decodedToken = token ? decodeJwt(token) : null;
   const hasToken = !!decodedToken && !isTokenExpired(decodedToken.exp) && isSameTenant(decodedToken.tenantId, hotel);
   const { data: chatHistory } = useChatHistoryQuery(hasToken);

   // Reconcile every history snapshot (page refresh, post-send refetch, reconnect
   // recovery) into what is on screen: update in place, never duplicate or drop.
   useEffect(() => {
      if (chatHistory && hasToken) {
         setMessages((prev) => reconcileMessages(prev, chatHistory.messages));
      }
   }, [chatHistory, setMessages, hasToken]);

   const getCurrentTime = useCallback((): string => {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
   }, []);

   /** A chat token was issued (Google sign-in): store it and load the conversation. An empty one shows the greeting. */
   const startSession = useCallback(
      async (issuedToken: string) => {
         // FIRST: Store the token so axios has it for subsequent requests
         setChatToken(issuedToken);

         // Remove the sign-in card immediately
         setMessages((prev: Message[]) => prev.filter((x: Message) => x.sender !== 'userDetails-bot'));

         // Wait for the re-render that enables the history query
         await new Promise((resolve) => setTimeout(resolve, 300));

         // THEN: Force refetch chat history with the new token
         await queryClient.refetchQueries({ queryKey: chatKeys.history(), type: 'active' });

         // Get the fresh data from the cache
         const freshHistory = queryClient.getQueryData<ChatHistory>(chatKeys.history());

         setMessages((prev) =>
            reconcileMessages(
               prev.filter((x: Message) => x.sender !== 'userDetails-bot'),
               freshHistory?.messages ?? []
            )
         );
      },
      [setMessages, queryClient]
   );

   /** The link has no hotel, or one that does not exist: signing in cannot help, so say so instead of offering it. */
   const showInvalidLink = useCallback(() => {
      setMessages([{ id: `invalid-link-${Date.now()}`, sender: 'bot', timestamp: getCurrentTime(), text: getStringsRef.current().invalidLink, notice: true }]);
   }, [setMessages, getCurrentTime]);

   /** "Sign in with Google": the API verifies the Google-signed ID token and issues the chat token. */
   const handleGoogleAuth = useCallback(
      async (idToken: string) => {
         try {
            if (!hotel) {
               showInvalidLink();
               return;
            }
            const signIn = await ChatService.signInWithGoogle({ hotelId: hotel, idToken });
            if (signIn.status === 'hotel-not-found') {
               showInvalidLink();
               return;
            }
            if (signIn.status !== 'signed-in') return;
            await startSession(signIn.token);
         } catch (error) {
            showErrorBanner(getStringsRef.current().signInFailed);
         }
      },
      [hotel, startSession, showInvalidLink]
   );

   /** Clears the session and shows the sign-in card, optionally with a notice (expired session, other hotel). */
   const showSignIn = useCallback(
      (noticeKey?: SignInNoticeKey) => {
         clearChatToken();
         const welcomeCard: Message = {
            id: `welcome-${Date.now()}`,
            sender: 'userDetails-bot',
            timestamp: getCurrentTime(),
            text: React.createElement(UserDetailsCard, {
               onGoogleAuth: async (idToken: string) => await handleGoogleAuth(idToken),
               noticeKey
            })
         };
         setMessages([welcomeCard]);
      },
      [setMessages, getCurrentTime, handleGoogleAuth]
   );

   useEffect(() => {
      if (hasInitiated.current) return;
      hasInitiated.current = true;

      if (!hotel) {
         showInvalidLink();
      } else if (!decodedToken?.userId) {
         showSignIn();
      } else if (!isSameTenant(decodedToken.tenantId, hotel)) {
         showSignIn('switchedHotel');
      } else if (isTokenExpired(decodedToken.exp)) {
         showSignIn('sessionExpired');
      }
      // Otherwise the token is valid for this hotel and the history query loads the conversation.
   }, [decodedToken, hotel, showSignIn, showInvalidLink]);

   // A 401 from the API or SignalR ends the session: back to sign-in (once) instead of a stuck chat.
   // Signing in again with the same Google account restores the conversation.
   useEffect(
      () =>
         onSessionExpired(() => {
            if (!getChatToken()) return; // already signed out
            queryClient.removeQueries({ queryKey: chatKeys.history() });
            showSignIn('sessionExpired');
         }),
      [queryClient, showSignIn]
   );

   return {
      canTypeMessages: hasToken,
      preferredLanguage: chatHistory?.preferredLanguage,
      hotelName: chatHistory?.hotelName,
      suggestedQuestions: chatHistory?.suggestedQuestions
   };
};
